/* eslint-disable @typescript-eslint/no-shadow */
import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import Tabs, { TabPane } from 'rc-tabs';
import Icon from '../../../../../shared/design-system/components/atoms/icon';
import Button from '../../../../../shared/design-system/components/atoms/button';
import { GetTemplatesRequest } from '../../../../templates/types/request';
import { RequestStatus } from '../../../../../shared/enums/request-status';
import {
  getInitailFilters,
  TabsKeys,
  saleshandyTemplatesLibrary,
} from './utils';
import { Template } from './types';
import TemplatesList from './components/templates-list';
import Search from './components/search-box';
import type { IProps } from './template-modal-container';
import toaster, { Theme } from '../../../../../shared/toaster/index';
import { executeOnRequestStatus } from '../../../../../shared/utils/execute-on-request-status';
import { showGeneralErrorMessage } from '../../../../templates/components/helpers';
import { isRequestPending } from '../../../../settings/components/billing-subscription/utils/helper';
import EmptyList from '../../../../../shared/design-system/components/molecules/empty-list/empty-list';
import { Images } from '../../../../../shared/app-constants';
import { ProductTour } from '../../../../../shared/enums/product-tour';
import hasPermission from '../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../shared/utils/access-control/enums/permissions';
import Attachment from '../../../../../shared/components/attachment';
import { AttachmentUploadStatus } from '../../../../../shared/editor/types';

const TemplateModal: React.FC<IProps> = ({
  show,
  onClose,
  onTemplateChange,
  myTemplates,
  getMyTemplateRequestStatus,
  sendGetMyTemplateRequest,
  teamTemplates,
  getTeamTemplateRequestStatus,
  sendGetTeamTemplateRequest,
  sendCreateTemplateRequest,
  createTemplateRequestStatus,
  createTemplateRequestMessage,
  createTemplateRequestError,
  resetTemplateState,
}) => {
  const [activeTabKey, setActiveTabKey] = useState<string>(
    TabsKeys.SALESHANDY_LIBRARY,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(null);
  const [isContentEmpty, setIsContentEmpty] = useState<boolean>(false);
  // My Templates State
  const [
    isMyTemplateTableSearched,
    setIsMyTemplateTableSearched,
  ] = useState<boolean>(false);
  const [
    myTemplateTableFilters,
    setMyTemplateTableFilters,
  ] = useState<GetTemplatesRequest>(getInitailFilters(TabsKeys.MY_TEMPLATES));

  // Team Templates State
  const [
    isTeamTemplateTableSearched,
    setIsTeamTemplateTableSearched,
  ] = useState<boolean>(false);
  const [
    teamTemplateTableFilters,
    setTeamTemplateTableFilters,
  ] = useState<GetTemplatesRequest>(getInitailFilters(TabsKeys.TEAM_TEMPLATES));

  // Template Library State
  const [templateLibrary, setTemplateLibrary] = useState<any[]>(
    saleshandyTemplatesLibrary,
  );

  // Get My Template Request
  useEffect(() => {
    if (show && hasPermission(Permissions.MY_TEMPLATES_READ)) {
      sendGetMyTemplateRequest(myTemplateTableFilters);
    }
  }, [myTemplateTableFilters, sendGetMyTemplateRequest, show]);

  // Get Team Template Request
  useEffect(() => {
    if (show && hasPermission(Permissions.TEAM_TEMPLATES_READ)) {
      sendGetTeamTemplateRequest(teamTemplateTableFilters);
    }
  }, [teamTemplateTableFilters, show, sendGetTeamTemplateRequest]);

  // Get Request Status
  const isMyTemplateRequestPending =
    getMyTemplateRequestStatus === RequestStatus.Ideal ||
    getMyTemplateRequestStatus === RequestStatus.Pending;

  const isTeamTemplateRequestPending =
    getTeamTemplateRequestStatus === RequestStatus.Ideal ||
    getTeamTemplateRequestStatus === RequestStatus.Pending;

  const resetState = () => {
    setIsMyTemplateTableSearched(false);
    setMyTemplateTableFilters(getInitailFilters(TabsKeys.MY_TEMPLATES));
    setTeamTemplateTableFilters(getInitailFilters(TabsKeys.TEAM_TEMPLATES));
    setIsMyTemplateTableSearched(false);
  };

  const onCloseHandler = () => {
    resetState();
    onClose();
  };

  const onSearch = (search: string, key: string, onClear?: boolean) => {
    if (key === TabsKeys.MY_TEMPLATES) {
      if (search !== myTemplateTableFilters.search) {
        setMyTemplateTableFilters({ ...myTemplateTableFilters, search });
        setIsMyTemplateTableSearched(true);
      }
      if (search === '') {
        if (onClear) {
          setMyTemplateTableFilters({ ...myTemplateTableFilters, search });
        }
        setIsMyTemplateTableSearched(false);
      }
    } else if (key === TabsKeys.SALESHANDY_LIBRARY) {
      if (search) {
        const result = saleshandyTemplatesLibrary
          .map((category) => category.templates)
          .flat()
          .filter(
            (product) =>
              product.subject.toLowerCase().includes(search.toLowerCase()) ||
              product.template.title
                .toLowerCase()
                .includes(search.toLowerCase()),
          );

        setTemplateLibrary([{ templates: result }]);
        return;
      }

      setTemplateLibrary(saleshandyTemplatesLibrary);
    } else {
      if (search !== teamTemplateTableFilters.search) {
        setTeamTemplateTableFilters({ ...teamTemplateTableFilters, search });
        setIsTeamTemplateTableSearched(true);
      }
      if (search === '') {
        if (onClear) {
          setTeamTemplateTableFilters({ ...teamTemplateTableFilters, search });
        }
        setIsTeamTemplateTableSearched(false);
      }
    }
  };

  const onTabChange = (tabKey) => {
    setActiveTabKey(tabKey);
    setTemplateLibrary(saleshandyTemplatesLibrary);
  };

  const getTemplates = (templates, id) =>
    templates.find(({ id: templateId }) => templateId === id);

  const selectTemplateContent = (id, tabKey) => {
    let templates = myTemplates;

    if (tabKey === TabsKeys.TEAM_TEMPLATES) {
      templates = teamTemplates;
    }

    setSelectedTemplate(getTemplates(templates, id));
  };

  const selectSaleshandyTemplate = (id) => {
    const templates = templateLibrary.find(({ templates }) =>
      getTemplates(templates, id),
    );

    if (templates) {
      setSelectedTemplate(getTemplates(templates.templates, id));
      setIsContentEmpty(false);
    } else {
      setIsContentEmpty(true);
    }
  };

  // const onCopyContent = () => {
  //   const text = htmlToText(selectedTemplate?.content);
  //   navigator.clipboard.writeText(text);
  //   toaster.success('Copied to Clipboard', {
  //     theme: Theme.New,
  //   });
  // };

  const checkTemplateAlreadyInMyList = () =>
    myTemplates.some(
      (item) =>
        item.subject === selectedTemplate.subject &&
        item.template.title === selectedTemplate.template.title,
    );

  const createTemplate = () => {
    if (!checkTemplateAlreadyInMyList()) {
      sendCreateTemplateRequest({
        title: selectedTemplate?.template?.title,
        subject: selectedTemplate?.subject,
        content: selectedTemplate?.content,
        attachmentIds: [],
      });
      return;
    }

    toaster.error('Template already exist', { theme: Theme.New });
  };

  useEffect(
    () => () => {
      resetTemplateState();
    },
    [],
  );

  useEffect(() => {
    executeOnRequestStatus({
      status: createTemplateRequestStatus,

      onSuccess: () => {
        sendGetMyTemplateRequest(myTemplateTableFilters);
        sendGetTeamTemplateRequest(teamTemplateTableFilters);
        toaster.success(createTemplateRequestMessage, { theme: Theme.New });
      },
      onFailed: () => {
        showGeneralErrorMessage(createTemplateRequestError);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTemplateRequestStatus]);

  useEffect(() => {
    if (activeTabKey === TabsKeys.MY_TEMPLATES && myTemplates.length === 0) {
      setIsContentEmpty(true);
    } else if (
      activeTabKey === TabsKeys.TEAM_TEMPLATES &&
      teamTemplates.length === 0
    ) {
      setIsContentEmpty(true);
    } else if (
      activeTabKey === TabsKeys.SALESHANDY_LIBRARY &&
      templateLibrary.length === 0
    ) {
      setIsContentEmpty(true);
    } else {
      setIsContentEmpty(false);
    }
  }, [activeTabKey, isMyTemplateRequestPending, isTeamTemplateRequestPending]);

  return (
    <Modal
      show={show}
      className="template-modal"
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>Email Templates</Modal.Title>
        <Button className="close" onClick={onCloseHandler}>
          <Icon identifier="close" />
        </Button>
      </Modal.Header>

      <Modal.Body className="d-flex">
        <div className="tabs-wrapper">
          <Tabs
            key={activeTabKey}
            defaultActiveKey={TabsKeys.SALESHANDY_LIBRARY}
            activeKey={activeTabKey}
            prefixCls="bs-tabs"
            className="bs-tabs-small"
            onChange={(tabKey) => onTabChange(tabKey)}
          >
            <TabPane tab="Saleshandy Library" key={TabsKeys.SALESHANDY_LIBRARY}>
              <Search
                isSearched={isTeamTemplateTableSearched}
                onSearch={onSearch}
                tabKey={TabsKeys.SALESHANDY_LIBRARY}
                isTemplateRequestPending={isTeamTemplateRequestPending}
              />
              <TemplatesList
                tabKey={TabsKeys.SALESHANDY_LIBRARY}
                templates={teamTemplates}
                isTemplateRequestPending={isTeamTemplateRequestPending}
                selectTemplateContent={(id) => selectSaleshandyTemplate(id)}
                saleshandyTemplatesLibrary={templateLibrary}
              />
            </TabPane>

            {hasPermission(Permissions.MY_TEMPLATES_READ) && (
              <TabPane tab="My Templates" key={TabsKeys.MY_TEMPLATES}>
                <Search
                  isSearched={isMyTemplateTableSearched}
                  onSearch={onSearch}
                  tabKey={TabsKeys.MY_TEMPLATES}
                  isTemplateRequestPending={isMyTemplateRequestPending}
                />
                <TemplatesList
                  tabKey={TabsKeys.MY_TEMPLATES}
                  templates={myTemplates}
                  isTemplateRequestPending={isMyTemplateRequestPending}
                  selectTemplateContent={(id, tabKey) =>
                    selectTemplateContent(id, tabKey)
                  }
                />
              </TabPane>
            )}
            {hasPermission(Permissions.TEAM_TEMPLATES_READ) && (
              <TabPane tab="Team Templates" key={TabsKeys.TEAM_TEMPLATES}>
                <Search
                  isSearched={isTeamTemplateTableSearched}
                  onSearch={onSearch}
                  tabKey={TabsKeys.TEAM_TEMPLATES}
                  isTemplateRequestPending={isTeamTemplateRequestPending}
                />
                <TemplatesList
                  tabKey={TabsKeys.TEAM_TEMPLATES}
                  templates={teamTemplates}
                  isTemplateRequestPending={isTeamTemplateRequestPending}
                  selectTemplateContent={(id, tabKey) =>
                    selectTemplateContent(id, tabKey)
                  }
                />
              </TabPane>
            )}
          </Tabs>
        </div>

        <div className="template-content mt-4 w-100">
          {!isContentEmpty ? (
            <>
              <div>
                <p className="regular-3 font-medium gray-txt-17">
                  Subject: {selectedTemplate?.subject || '<Empty Subject>'}{' '}
                </p>

                <div className="template-content__content mt-4 gray-19">
                  <div
                    className="p-4 regular-2 popover-arrow-color-txt"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: selectedTemplate?.content,
                    }}
                  />
                  {selectedTemplate &&
                    selectedTemplate.template.templateAttachments &&
                    selectedTemplate.template.templateAttachments.length >
                      0 && (
                      <div className="template-content__attachments mt-4">
                        <div className="template-content__attachments-list d-flex align-items-center mt-4 email-preview-attachments">
                          {selectedTemplate.template.templateAttachments.map(
                            (temp) => (
                              <Attachment
                                key={temp.attachmentId}
                                attachment={{
                                  attachmentId: temp.attachmentId,
                                  file: temp.attachment,
                                  attachmentSize: Number(temp.attachment.size),
                                  status: AttachmentUploadStatus.Existing,
                                }}
                                showAttachmentDeleteButton={false}
                              />
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>

              <div className="template-content__actions pt-4 pb-4">
                <div className="template-content__actions-button d-flex justify-content-between align-items-center">
                  {/* <Button
                    variant={Button.Variant.Outlined}
                    onClick={onCopyContent}
                  >
                    Copy
                  </Button> */}
                  {activeTabKey === TabsKeys.SALESHANDY_LIBRARY && (
                    <Button
                      variant={Button.Variant.Primary}
                      theme={Button.Theme.Ghost}
                      className="ml-3 mr-3"
                      onClick={createTemplate}
                      disabled={isRequestPending(createTemplateRequestStatus)}
                    >
                      Save to My Templates
                    </Button>
                  )}
                  <Button
                    id={ProductTour.UseTemplate}
                    variant={Button.Variant.Primary}
                    onClick={() => onTemplateChange(selectedTemplate)}
                  >
                    Use Template
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <EmptyList
                title="No Template Found"
                description=""
                isVertical={true}
                imgSrc={Images.NoResult}
              />
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default TemplateModal;
